import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Check, X, ShieldCheck, RefreshCw, ExternalLink } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { devError } from '../utils/logger';
import { toast } from 'sonner';

// Allowlist of platform-admin emails — kept in sync with backend `ADMIN_EMAILS` env var.
const ADMIN_EMAILS = ['thisisradas@gmail.com'];

/**
 * Admin-only supplier verification queue at `/admin/suppliers`.
 *
 * New supplier registrations land in `pending` state and stay hidden from the
 * public marketplace until an admin eyeballs them here and clicks Approve.
 */
export default function AdminSuppliersPage() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null); // user_id currently being acted on
  const [rejectReason, setRejectReason] = useState({});

  const isAdmin = !!user && (user.user_type === 'admin' || (user.email && ADMIN_EMAILS.includes(user.email.toLowerCase())));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/suppliers/pending`);
      setItems(data.items || []);
    } catch (e) {
      devError('admin-suppliers-pending', e);
      toast.error(e.response?.data?.detail || 'Failed to load pending suppliers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
  }, [isAdmin]);

  if (authLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-accent animate-spin" />
      </div>
    );
  }
  if (!isAdmin) return <Navigate to="/" replace />;

  const moderate = async (userId, approve) => {
    setActing(userId);
    try {
      await axios.post(`${API}/admin/suppliers/verify`, {
        user_id: userId,
        approve,
        note: approve ? null : (rejectReason[userId] || null),
      });
      toast.success(approve ? 'Supplier approved — they are now live' : 'Supplier rejected');
      setItems((prev) => prev.filter((p) => p.user_id !== userId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="admin-suppliers-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2 inline-flex items-center gap-2">
              <ShieldCheck className="w-3 h-3" /> Supplier verification queue
            </p>
            <h1 className="font-playfair text-3xl md:text-4xl font-bold mb-2">
              Pending suppliers
            </h1>
            <p className="text-sm text-muted-foreground">
              {loading ? 'Loading…' : `${items.length} supplier${items.length === 1 ? '' : 's'} awaiting review`}
            </p>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-suppliers-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-accent animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 rounded-xl border border-border/20 bg-card/30">
            <ShieldCheck className="w-10 h-10 text-emerald-400/60 mx-auto mb-3" />
            <p className="text-muted-foreground">All caught up — every supplier is approved.</p>
          </div>
        ) : (
          <div className="space-y-5" data-testid="admin-suppliers-list">
            {items.map((s) => (
              <article
                key={s.user_id}
                className="rounded-xl border border-border/30 bg-card/30 p-5 md:p-6"
                data-testid={`pending-supplier-${s.user_id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <h3 className="font-playfair text-xl font-semibold text-foreground mb-1">
                      {s.company_name || s.name || 'Untitled supplier'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {s.name && s.company_name ? `${s.name} • ` : ''}{s.email}
                    </p>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                    Pending
                  </span>
                </div>

                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-4">
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">Phone</dt>
                    <dd className="text-foreground/90">{s.phone || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">Location</dt>
                    <dd className="text-foreground/90">{s.location || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">Registered</dt>
                    <dd className="text-foreground/90">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">Draft listings</dt>
                    <dd className="text-foreground/90">{s.listings_count || 0}</dd>
                  </div>
                </dl>

                {/* Optional reviewer note for rejections */}
                <div className="mt-5">
                  <label htmlFor={`note-${s.user_id}`} className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">
                    Note to supplier (sent by email if rejecting)
                  </label>
                  <Textarea
                    id={`note-${s.user_id}`}
                    rows={2}
                    placeholder="Optional — e.g. 'We couldn't verify your business. Please reply with a link to your company website.'"
                    value={rejectReason[s.user_id] || ''}
                    onChange={(e) => setRejectReason((r) => ({ ...r, [s.user_id]: e.target.value }))}
                    data-testid={`reject-note-${s.user_id}`}
                    className="text-sm bg-background/40"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => moderate(s.user_id, false)}
                    disabled={acting === s.user_id}
                    data-testid={`reject-supplier-${s.user_id}`}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => moderate(s.user_id, true)}
                    disabled={acting === s.user_id}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                    data-testid={`approve-supplier-${s.user_id}`}
                  >
                    {acting === s.user_id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Approve &amp; go live
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-10">
          <ExternalLink className="w-3 h-3 inline mr-1" />
          Approved suppliers appear instantly on the marketplace, /suppliers map, and their
          storefront becomes public. They also get an approval email.
        </p>
      </div>
    </div>
  );
}
